import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Upload, FileText, CheckCircle2, XCircle, AlertTriangle,
  Clock, Download, RefreshCw, ChevronRight, History, Shield,
} from "lucide-react";
import { PageHeader } from "../components/layout/Shell";
import { ResponsivePageContainer } from "../components/shared/ResponsivePageContainer";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { api as axios } from "../lib/api";

const API = "";

const CLASSIFICATION_META = {
  ACCEPTED_AUTHORITATIVE:    { color: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Accepted — Authoritative" },
  ACCEPTED_MIGRATION_SOURCE: { color: "text-blue-700 bg-blue-50 border-blue-200",   label: "Accepted — Migration Source" },
  REJECTED_INCOMPLETE:       { color: "text-red-700 bg-red-50 border-red-200",       label: "Rejected — Incomplete" },
  REJECTED_CONTRADICTORY:    { color: "text-red-700 bg-red-50 border-red-200",       label: "Rejected — Contradictory" },
  REJECTED_IDENTITY_AMBIGUITY: { color: "text-orange-700 bg-orange-50 border-orange-200", label: "Rejected — Identity Ambiguity" },
  REJECTED_PROGRAM_AMBIGUITY:  { color: "text-orange-700 bg-orange-50 border-orange-200", label: "Rejected — Program Ambiguity" },
};

const ROW_STATUS_META = {
  PENDING_ENROLLMENT_CREATION: { icon: CheckCircle2, color: "text-emerald-600", label: "Accepted" },
  ROW_REJECTED:                { icon: XCircle,      color: "text-red-600",     label: "Rejected" },
  ROW_SKIPPED:                 { icon: AlertTriangle, color: "text-amber-600",   label: "Skipped" },
  ENROLLMENT_CREATED:          { icon: CheckCircle2, color: "text-emerald-700", label: "Created" },
};

// Active tab enum
const TAB = { UPLOAD: "upload", RESULTS: "results", HISTORY: "history" };

export default function EnrollmentImport() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [tab, setTab]               = useState(TAB.UPLOAD);
  const [dragOver, setDragOver]     = useState(false);
  const [selectedFile, setFile]     = useState(null);
  const [running, setRunning]       = useState(false);
  const [runResult, setRunResult]   = useState(null);  // dry-run response
  const [runs, setRuns]             = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [showCommitDialog, setShowCommitDialog] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);

  const canCommit  = ["institution_admin", "super_admin"].includes(user?.role);
  const canDryRun  = ["registrar", "institution_admin", "super_admin"].includes(user?.role);

  // ── Fetch run history ─────────────────────────────────────────────────
  const fetchRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const res = await axios.get(`/enrollment/import/runs`);
      setRuns(res.data.items || []);
    } catch {
      toast.error("Failed to load run history.");
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === TAB.HISTORY) fetchRuns();
  }, [tab, fetchRuns]);

  // ── File selection ────────────────────────────────────────────────────
  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f && f.name.endsWith(".csv")) { setFile(f); }
    else toast.error("Please drop a .csv file.");
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  // ── Download template ─────────────────────────────────────────────────
  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`/enrollment/import/template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "enr_sod1_canonical_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download template.");
    }
  };

  // ── Run dry-run ───────────────────────────────────────────────────────
  const runDryRun = async () => {
    if (!selectedFile) { toast.error("Select a CSV file first."); return; }
    setRunning(true);
    try {
      const form = new FormData();
      form.append("file", selectedFile);
      const res = await axios.post(`/enrollment/import/dry-run`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRunResult(res.data);
      setTab(TAB.RESULTS);
      toast.success(`Dry run complete: ${res.data.rows_accepted} accepted, ${res.data.rows_rejected} rejected.`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === "object" && d?.reason_code) {
        toast.error(`Stage ${d.stage} — ${d.reason_code}: ${d.message}`);
      } else {
        toast.error(typeof d === "string" ? d : "Dry run failed.");
      }
    } finally {
      setRunning(false);
    }
  };

  // ── Commit (Stage 9) ──────────────────────────────────────────────────
  const commitImport = async () => {
    if (!runResult?.run_id) return;
    setCommitting(true);
    try {
      const res = await axios.post(`/enrollment/import/commit/${runResult.run_id}`);
      toast.success(`Import committed — ${res.data.enrollments_created} enrollment(s) created.`);
      setShowCommitDialog(false);
      setRunResult(prev => ({ ...prev, _committed: true, _commit_result: res.data }));
      fetchRuns();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Commit failed.");
    } finally {
      setCommitting(false);
    }
  };

  // ── Fetch single run detail ───────────────────────────────────────────
  const loadRunDetail = async (runId) => {
    try {
      const res = await axios.get(`/enrollment/import/runs/${runId}`);
      setSelectedRun(res.data);
    } catch {
      toast.error("Failed to load run detail.");
    }
  };

  const classMeta = runResult
    ? CLASSIFICATION_META[runResult.source_classification] || { color: "text-slate-600 bg-slate-50 border-slate-200", label: runResult.source_classification }
    : null;

  return (
    <>
      <PageHeader
        eyebrow="Student Lifecycle / Enrollment Onboarding"
        title="Import Students"
        description="Upload a canonical CSV to validate and authorize enrollment creation via the ENR-SOD1 pipeline."
      />
      <ResponsivePageContainer>
        {/* Tabs */}
        <div className="mb-6 flex gap-1 rounded-lg border border-border bg-muted/30 p-1 w-fit">
          {[
            { id: TAB.UPLOAD,  label: "Upload & Validate" },
            { id: TAB.RESULTS, label: "Dry Run Results", disabled: !runResult },
            { id: TAB.HISTORY, label: "Run History" },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => !t.disabled && setTab(t.id)}
              disabled={t.disabled}
              data-testid={`tab-${t.id}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                tab === t.id
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ─── TAB: UPLOAD ─────────────────────────────────────────────── */}
        {tab === TAB.UPLOAD && (
          <div className="max-w-2xl space-y-6">
            {/* Template download */}
            <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50/60 p-4">
              <FileText className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-blue-900">Canonical CSV Template</p>
                <p className="mt-0.5 text-xs text-blue-700">
                  Download the template generated from the frozen ENR-SOD1 import contract.
                  It includes all required columns and a field guide.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadTemplate}
                className="shrink-0 border-blue-300 text-blue-700 hover:bg-blue-100"
                data-testid="download-template-btn"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Download
              </Button>
            </div>

            {/* File drop zone */}
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleFileDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="csv-drop-zone"
              className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
                dragOver
                  ? "border-primary bg-primary/5"
                  : selectedFile
                  ? "border-emerald-300 bg-emerald-50/40"
                  : "border-border hover:border-primary/50 hover:bg-muted/20"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileSelect}
                data-testid="csv-file-input"
              />
              {selectedFile ? (
                <>
                  <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-500" />
                  <p className="text-sm font-medium text-foreground">{selectedFile.name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {(selectedFile.size / 1024).toFixed(1)} KB · Click to change
                  </p>
                </>
              ) : (
                <>
                  <Upload className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                  <p className="text-sm font-medium text-foreground">
                    Drop your CSV here or click to select
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    UTF-8 CSV with ENR-SOD1 canonical columns
                  </p>
                </>
              )}
            </div>

            {/* Notes */}
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 text-xs text-amber-800 space-y-1">
              <p className="font-semibold">Validation rules (ENR-SOD1)</p>
              <ul className="list-disc list-inside space-y-0.5 ml-1">
                <li>CSV tenant_id and institution_id must match your authenticated institution.</li>
                <li>No fuzzy matching — exact student_number or PG_PERSON_ID resolution only.</li>
                <li>A ProgramOffering must exist for the program + academic year before import.</li>
                <li>Dates must come from the source record — no system defaults accepted.</li>
                <li>Stage 9 (enrollment creation) requires separate authorization by institution_admin.</li>
              </ul>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              {canDryRun && (
                <Button
                  onClick={runDryRun}
                  disabled={!selectedFile || running}
                  data-testid="run-dry-run-btn"
                >
                  {running ? (
                    <><RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />Validating…</>
                  ) : (
                    <><ChevronRight className="mr-1.5 h-4 w-4" />Run Dry Validation (Stages 1–8)</>
                  )}
                </Button>
              )}
              <Button variant="outline" onClick={() => setTab(TAB.HISTORY)} data-testid="view-history-btn">
                <History className="mr-1.5 h-4 w-4" />
                View History
              </Button>
            </div>
          </div>
        )}

        {/* ─── TAB: RESULTS ────────────────────────────────────────────── */}
        {tab === TAB.RESULTS && runResult && (
          <div className="space-y-5" data-testid="dry-run-results">
            {/* Summary header */}
            <div className="flex flex-wrap items-center gap-3">
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${classMeta?.color}`}>
                {classMeta?.label}
              </span>
              {runResult._committed && (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Import committed
                </span>
              )}
              <span className="text-xs text-muted-foreground">Run ID: {runResult.run_id}</span>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { label: "Submitted", value: runResult.rows_submitted, color: "text-foreground" },
                { label: "Accepted",  value: runResult.rows_accepted,  color: "text-emerald-700" },
                { label: "Rejected",  value: runResult.rows_rejected,  color: "text-red-600" },
                { label: "Skipped",   value: runResult.rows_skipped,   color: "text-amber-600" },
              ].map(m => (
                <div key={m.label} className="rounded-lg border border-border bg-card p-4">
                  <div className={`text-2xl font-bold tabular-nums ${m.color}`}>{m.value}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{m.label}</div>
                </div>
              ))}
            </div>

            {/* Commit result */}
            {runResult._commit_result && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                <strong>Stage 9 complete:</strong> {runResult._commit_result.enrollments_created} enrollment(s) created
                {runResult._commit_result.rows_skipped > 0 && ` · ${runResult._commit_result.rows_skipped} skipped (already exist)`}
                {runResult._commit_result.rows_failed > 0 && ` · ${runResult._commit_result.rows_failed} failed`}.
                Committed by {runResult._commit_result.committed_by}.
              </div>
            )}

            {/* Row results table */}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Row Results</h3>
              <div className="overflow-hidden rounded-xl border border-border bg-card">
                <div className="table-scroll-container">
                  <table className="w-full text-xs" data-testid="row-results-table">
                    <thead>
                      <tr className="border-b border-border bg-muted/40">
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">#</th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">Status</th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">Student</th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">Program</th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">Year</th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">Reason</th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">Stage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {runResult.rows?.map(r => {
                        const meta = ROW_STATUS_META[r.row_status] || {};
                        const Icon = meta.icon;
                        return (
                          <tr key={r.row_number} className="hover:bg-muted/20" data-testid={`result-row-${r.row_number}`}>
                            <td className="px-3 py-2 font-mono text-muted-foreground">{r.row_number}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center gap-1 ${meta.color}`}>
                                {Icon && <Icon className="h-3.5 w-3.5" />}
                                {meta.label || r.row_status}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-foreground">{r.student_identifier}</td>
                            <td className="px-3 py-2 text-muted-foreground">{r.program_code}</td>
                            <td className="px-3 py-2 text-muted-foreground">{r.academic_year_label}</td>
                            <td className="px-3 py-2 max-w-[240px]">
                              {r.reason_code ? (
                                <span className="text-red-600 font-mono">{r.reason_code}</span>
                              ) : "—"}
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">
                              {r.pipeline_stage_failed ? `Stage ${r.pipeline_stage_failed}` : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Authorize Import button */}
            {canCommit && runResult.can_commit && !runResult._committed && (
              <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
                <Shield className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-emerald-900">Authorize Enrollment Import</p>
                  <p className="mt-0.5 text-xs text-emerald-700">
                    {runResult.rows_accepted} row(s) are ready. Stage 9 will create governed ProgramEnrollment
                    records. This action is irreversible and will be audit-logged.
                  </p>
                </div>
                <Button
                  size="sm"
                  className="shrink-0 bg-emerald-700 hover:bg-emerald-800 text-white"
                  onClick={() => setShowCommitDialog(true)}
                  data-testid="authorize-import-btn"
                >
                  Authorize Import
                </Button>
              </div>
            )}

            {!canCommit && runResult.can_commit && !runResult._committed && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                <strong>Note:</strong> {runResult.rows_accepted} row(s) are validated and ready.
                Authorization requires <strong>institution_admin</strong> or <strong>super_admin</strong> role.
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" size="sm" onClick={() => { setTab(TAB.UPLOAD); setFile(null); }} data-testid="upload-another-btn">
                Upload Another File
              </Button>
            </div>
          </div>
        )}

        {/* ─── TAB: HISTORY ────────────────────────────────────────────── */}
        {tab === TAB.HISTORY && (
          <div className="space-y-4" data-testid="run-history">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Import Run History</h3>
              <Button variant="outline" size="sm" onClick={fetchRuns} disabled={runsLoading} data-testid="refresh-history-btn">
                <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${runsLoading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>

            {runsLoading ? (
              <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground justify-center">
                <RefreshCw className="h-4 w-4 animate-spin" />Loading…
              </div>
            ) : runs.length === 0 ? (
              <div className="flex flex-col items-center py-12 text-center text-muted-foreground">
                <Clock className="mb-2 h-7 w-7 opacity-40" />
                <p className="text-sm">No runs yet. Upload a CSV and run a dry validation first.</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border bg-card">
                <div className="table-scroll-container">
                  <table className="w-full text-sm" data-testid="run-history-table">
                    <thead>
                      <tr className="border-b border-border bg-muted/40">
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Type</th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Source System</th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Classification</th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Rows</th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Run At</th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">By</th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {runs.map(r => {
                        const cMeta = CLASSIFICATION_META[r.source_classification];
                        return (
                          <tr
                            key={r.id}
                            className="hover:bg-muted/20 cursor-pointer transition-colors"
                            onClick={() => loadRunDetail(r.id)}
                            data-testid={`history-run-${r.id}`}
                          >
                            <td className="px-4 py-3">
                              <Badge variant={r.run_type === "IMPORT" ? "default" : "secondary"}>
                                {r.run_type}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                              {r.source_system}
                            </td>
                            <td className="px-4 py-3">
                              {r.source_classification ? (
                                <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${cMeta?.color || "text-slate-600 bg-slate-50 border-slate-200"}`}>
                                  {cMeta?.label || r.source_classification}
                                </span>
                              ) : "—"}
                            </td>
                            <td className="px-4 py-3 text-xs text-muted-foreground">
                              {r.rows_submitted} total · {r.rows_accepted} ok · {r.rows_rejected} err
                            </td>
                            <td className="px-4 py-3 text-xs text-muted-foreground">
                              {r.run_at ? new Date(r.run_at).toLocaleString() : "—"}
                            </td>
                            <td className="px-4 py-3 text-xs text-muted-foreground truncate max-w-[140px]">
                              {r.run_by_email}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-medium ${r.run_status === "COMPLETED" ? "text-emerald-600" : r.run_status === "FAILED" ? "text-red-600" : "text-amber-600"}`}>
                                {r.run_status}
                                {r.committed_at ? " · committed" : ""}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Run detail panel */}
            {selectedRun && (
              <div className="mt-4 rounded-xl border border-border bg-card p-4 space-y-3" data-testid="run-detail-panel">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold">Run Detail — {selectedRun.run.run_type}</h4>
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setSelectedRun(null)}
                  >
                    Close
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                  <div><span className="text-muted-foreground">Source: </span>{selectedRun.run.source_system}</div>
                  <div><span className="text-muted-foreground">File: </span>{selectedRun.run.source_file_name}</div>
                  <div><span className="text-muted-foreground">By: </span>{selectedRun.run.run_by_email}</div>
                  <div><span className="text-muted-foreground">SHA-256: </span>
                    <span className="font-mono">{selectedRun.run.source_file_hash?.substring(0, 12)}…</span>
                  </div>
                </div>
                {selectedRun.rows?.length > 0 && (
                  <div className="overflow-hidden rounded-lg border border-border">
                    <div className="table-scroll-container max-h-64 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-muted/40 border-b border-border">
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground">#</th>
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Status</th>
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Student</th>
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Program</th>
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Reason</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {selectedRun.rows.map(r => {
                            const meta = ROW_STATUS_META[r.row_status] || {};
                            return (
                              <tr key={r.id} className="hover:bg-muted/20">
                                <td className="px-3 py-1.5 font-mono text-muted-foreground">{r.row_number}</td>
                                <td className={`px-3 py-1.5 font-medium ${meta.color}`}>{meta.label || r.row_status}</td>
                                <td className="px-3 py-1.5">{r.student_identifier}</td>
                                <td className="px-3 py-1.5 text-muted-foreground">{r.program_code}</td>
                                <td className="px-3 py-1.5 font-mono text-red-600">{r.reason_code || "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </ResponsivePageContainer>

      {/* Commit Confirmation Dialog */}
      <Dialog open={showCommitDialog} onOpenChange={setShowCommitDialog}>
        <DialogContent data-testid="commit-dialog">
          <DialogHeader>
            <DialogTitle>Authorize Enrollment Import — Stage 9</DialogTitle>
            <DialogDescription>
              This will create {runResult?.rows_accepted} governed ProgramEnrollment record(s).
              This action is <strong>irreversible</strong> and will be fully audit-logged.
              You are the committing authority.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1">
            <p><strong>Run ID:</strong> {runResult?.run_id}</p>
            <p><strong>Source classification:</strong> ACCEPTED_AUTHORITATIVE</p>
            <p><strong>Rows to enroll:</strong> {runResult?.rows_accepted}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommitDialog(false)} disabled={committing}>
              Cancel
            </Button>
            <Button
              onClick={commitImport}
              disabled={committing}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="confirm-commit-btn"
            >
              {committing ? "Committing…" : "Confirm — Create Enrollments"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
